import React, { useEffect, useState, useRef } from 'react';
import { useFetchResultQuery } from '../../api/result';
import convertS3UrlToHttps from '../../utils/convertS3UrlToHttps';
import { useFetchResultCompareQuery } from '../../api/resultCompare';
import classNames from 'classnames';
import syncPan from './ImageViewer/syncPan';
import syncZoom from './ImageViewer/syncZoom';
import registerOpenHandler from './ImageViewer/registerOpenHandler';
import registerRightClickHandler from './ImageViewer/registerRightClickHandler';
import ComapreHeading from './ImageViewer/Compare/CompareHeading';
import { useFetchProjectQuery } from '../../api/project';
import { useFetchImageQuery } from '../../api/image';
import { useDispatch, useSelector } from 'react-redux';
import { addTile, selectSelectionMode, selectSelectedTiles } from '../../Store/tileSelectionSlice';
import { useGetTilesQuery } from '../../api/tileSelection';
import ResultSidebar from './ResultSidebar';
import CompareButton from './CompareButton';
import { selectResultIdsState } from '../../Store/resultSlice';
import ResultViewerImageToolBar from './ResultViewerImageToolBar';
import { useSearchParams } from 'react-router-dom';

const OpenSeadragon = require('openseadragon');

interface ImageViewerProps {
    imageId: string;
    resultId: string;
    compare?: string;
    showResultList: boolean;
    onToggleResultList: () => void;
}

function parseTileCoords(url: string) {
    const match = url.match(/\/16\/(\d+)_(\d+)\.jpg$/);
    if (match) {
        return `${match[1]}_${match[2]}`;
    }
    return null;
}

const ImageViewer = (props: ImageViewerProps) => {
    const { resultId, compare, imageId, showResultList, onToggleResultList } = props;
    const [searchParams] = useSearchParams();
    const dataIndex = parseInt(searchParams.get('dataIndex') || '0');
    const { data: result, isLoading, isError } = useFetchResultQuery(resultId);
    const { data: image, isLoading: isLoadingImage, isError: isErrorImage } = useFetchImageQuery(result?.image_id);
    const { data: project, isLoading: isLoadingProject, isError: isErrorProject} = useFetchProjectQuery(image?.project_id);
    // compare
    const { data: resultCompare, isLoading: isLoadingCompare, isError: isErrorCompare } = useFetchResultCompareQuery(compare);
    const { data: imageCompare, isLoading: isLoadingImageCompare, isError: isErrorImageCompare } = useFetchImageQuery(resultCompare?.image_id);
    const { data: projectCompare, isLoading: isLoadingProjectCompare, isError: isErrorProjectCompare} = useFetchProjectQuery(imageCompare?.project_id);

    const selectionModeOn = useSelector(selectSelectionMode);
    const selectedTiles = useSelector(selectSelectedTiles);
    const selectedResultIds = useSelector(selectResultIdsState);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastType, setToastType] = useState<'warning' | 'success'>('success');
    const viewerCompareRef = useRef<any>(null);
    
    const dispatch = useDispatch();

    const { data: previousTiles } = useGetTilesQuery(
        { imageId: result?.image_id!, resultId },
        { skip: !result?.image_id }
    );

    const handleTileAdd = (tile: any) => {
        const tileCoord = parseTileCoords(tile.url);
        
        // Check if already in selected tiles
        const isAlreadySelected = selectedTiles.some(selectedTile => {
            const selectedCoord = parseTileCoords(selectedTile.url);
            return selectedCoord === tileCoord;
        });
        
        if (isAlreadySelected) {
            setToastMessage('Tile added for submission.');
            setToastType('success');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
            return;
        }
        
        // Check if in previously submitted tiles
        const isAlreadySubmitted = previousTiles?.some(prevTile => {
            const prevCoord = parseTileCoords(prevTile.source_tile_url);
            return prevCoord === tileCoord;
        });
        
        if (isAlreadySubmitted) {
            setToastMessage('This tile has already been submitted!');
            setToastType('warning');
            setShowToast(true);
            setTimeout(() => setShowToast(false), 3000);
            return;
        }
        
        // If we get here, it's a new tile
        dispatch(addTile({
            level: tile.level,
            url: tile.url,
            bounds: tile.bounds,
            position: tile.position,
            resultId,
        }));
    };

    useEffect(() => {
        let viewer: any = null;
        let viewerCompare: any = null;
        if (!isLoading && result && result.result_data) {
            // Use the dataIndex from URL, defaulting to 0 if not present
            const resultData = result.result_data[dataIndex] || result.result_data[0];
            const original = convertS3UrlToHttps(resultData['Images']['original']);
            const overlay = convertS3UrlToHttps(resultData['Images']['overlay']);

            viewer = OpenSeadragon({
                id: `openseadragon-${resultId}`,
                tileSources: [
                    original,
                    overlay,
                ],
                animationTime: 0.5,
                showNavigator: false,
                zoomInButton: "zoom-in",
                zoomOutButton: "zoom-out",
                homeButton: "home",
                fullPageButton: "full-page",
                nextButton: "next",
                previousButton: "previous",
                zoomPerClick: 2.0,
                maxZoomLevel: 16,
            });
            
            // Define a handler for canvas-click event to capture the clicked tile source at level 16
            viewer.addHandler('canvas-click', (event: any) => {
                if(selectionModeOn){
                    const clickedPoint = viewer.viewport.pointFromPixel(event.position);
                    const tiledImage = viewer.world.getItemAt(0);
                    const tileSource = tiledImage.lastDrawn.find((tile:any) => {
                        const bounds = tile.bounds;
                        return tile.level === 16 && bounds.containsPoint(clickedPoint);
                    });

                    if (tileSource) {
                        console.log(`Tile source URL at level ${tileSource.level}:`, tileSource.url);
                        handleTileAdd(tileSource);
                    }
                }
            });

            registerOpenHandler(viewer);
            registerRightClickHandler(viewer);

            if (compare && resultCompare) {
                const originalCompare = convertS3UrlToHttps(resultCompare.result_data[0]['Images']['original']);
                const overlayCompare = convertS3UrlToHttps(resultCompare.result_data[0]['Images']['overlay']);

                viewerCompare = OpenSeadragon({
                    id: `openseadragon-${compare}`,
                    tileSources: [
                        originalCompare,
                        overlayCompare,
                    ],
                    animationTime: 0.5,
                    showNavigator: false,
                    zoomInButton: "zoom-in",
                    zoomOutButton: "zoom-out",
                    homeButton: "home",
                    fullPageButton: "full-page",
                    nextButton: "next",
                    previousButton: "previous",
                });

                viewerCompareRef.current = viewerCompare;

                registerOpenHandler(viewerCompare);
                registerRightClickHandler(viewerCompare);
                syncPan(viewer, viewerCompare);
                syncPan(viewerCompare, viewer);
                syncZoom(viewer, viewerCompare);
                syncZoom(viewerCompare, viewer);
            }

            const slider: HTMLInputElement | null = document.querySelector('#image-viewer-opacity');
            if (slider) {
                slider.oninput = function () {
                    const opacityValue = Number(slider.value) / 100;
                    const overlay = viewer.world.getItemAt(1);
                    overlay?.setOpacity(opacityValue);

                    if (compare && resultCompare) {
                        const overlayCompare = viewerCompare.world.getItemAt(1);
                        overlayCompare?.setOpacity(opacityValue);
                    }
                }
            }
        }

        return () => {
            viewer?.destroy();
            viewerCompareRef.current?.destroy();
            viewerCompareRef.current = null;
        };
    }, [isLoading, isLoadingCompare, result, compare, resultCompare, dataIndex]);

    // Additional cleanup when exiting compare view
    useEffect(() => {
        if (!compare && viewerCompareRef.current) {
            viewerCompareRef.current.destroy();
            viewerCompareRef.current = null;
        }
    }, [compare]);

    const seaDragonBoxClass = classNames({
        'w-full': !compare,
        'w-1/2': !!compare,
    }, 'h-full')

    return (
        <div id='openseadragon' className='flex flex-col h-full w-full'>
            {showToast && (
                <div className={`fixed top-4 right-4 ${toastType === 'warning' ? 'bg-amber-500' : 'bg-green-500'} text-white px-4 py-2 rounded shadow-lg z-50 animate-fadeIn`}>
                    {toastMessage}
                    <button onClick={() => setShowToast(false)} className="ml-2 text-white">×</button>
                </div>
            )}
            {compare && <ComapreHeading currentProject={project} compareProject={projectCompare} result={result} resultCompare={resultCompare}/>}
            <div className='flex flex-row h-full w-full'>
                {showResultList && (
                    <div className='w-64 h-full flex flex-col border-r border-gray-200'>
                        <ResultSidebar imageId={imageId} />
                    </div>
                )}
                <div className='flex-1 flex flex-col'>
                    <div className='flex flex-row h-full'>
                        <div id={`openseadragon-${resultId}`} className={seaDragonBoxClass} />
                        {compare && <div id={`openseadragon-${compare}`} className={`${seaDragonBoxClass} compared-to`} />}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default ImageViewer;
